import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import { HttpClient } from '@angular/common/http';
import * as moment from 'moment-timezone';
moment.locale('es-US');
moment.tz.setDefault('America/Mexico_City');

import { Indicator } from '../../models/indicator.class';
import { IndicatorEntry } from '../../models/indicatorEntry.class';

/**
 * DataEnadisCompareService
 * Provides data read from sources
 */
@Injectable()
export class DataEnadisCompareService {
  /** List of indicators */
  codebook: Array<Indicator>;

  /** Inits service. Injects http client */
  constructor(private http: HttpClient) { }

  /**
   * Returns the list of indicators using a GET request
   * Can use filters depending on topic
   */
  getCodebook(topic?: string): Observable<Indicator[]> {
    return this.http.get<Indicator[]>('/api/enadis/v1/indicator?haveData=true' + (topic ? '&topic=' + topic : ''));
  }

  /** Returns one indicator based on an indicator identifier */
  getIndicator(indicator: string): Observable<Indicator[]> {
    return this.http.get<Indicator[]>('/api/enadis/v1/indicator?indicator=' + indicator);
  }

  /** Get entries optionally of a selected indicator and in a parsed mode */
  getEntries(indicator?, mode?): Observable<IndicatorEntry[]> {
    let url = '/api/enadis/v1/indicatorEntry?';
    if (indicator) {
      url += 'indicator=' + indicator + '&';
    }
    if (mode) {
      url += 'mode=' + mode;
    }
    return this.http.get<IndicatorEntry[]>(url);
  }

  /** Get columns that correspond to selected indicator */
  getColumnsFromIndicatorId(indicator: string): Observable<Object[]> {
    return this.http.get<Object[]>('/api/enadis/v1/catalog/getColumnsFromIndicator/' + indicator);
  }

  /** Get answers that correspond to selected indicator */
  getRowsFromIndicatorId(indicator: string): Observable<Object[]> {
    return this.http.get<Object[]>('/api/enadis/v1/catalog/getRowsFromIndicator/' + indicator);
  }

  /** Get filter options for a selected indicator and a specific column name */
  getFilterOptions(indicator: string, columnName: string): Observable<Object[]> {
    return this.http.get<Object[]>('/api/enadis/v1/indicatorEntry/getFilterOptions?indicator=' + indicator + '&columnName=' + columnName);
  }

  /**
   * Returns entries data, based on selected indicator, selected filters and option keys.
   * Data should be returned ready to display in a chart
   */
  getDataToDraw(indicator: string, selectedFilters: object, keys: object, frequency: string): Observable<IndicatorEntry[]> {
    const url = '/api/enadis/v1/indicatorEntry/getDataToDraw';
    const params = {
      indicator: indicator,
      units: selectedFilters['unidad'],
      keys: keys,
      selectedFilters: selectedFilters,
      frequency: frequency
    };
    return this.http.post<IndicatorEntry[]>(url, params);
  }

  /**
   * Returns data ready to display based on TWO selected indicators.
   * For scatter purposes. Every indicator should have its own specification of selected filters,
   * option keys, and units
   */
  crossData(firstIndicator: { indicator: string, units: string, selectedFilters: object, keys: object },
    secondIndicator: { indicator: string, units: string, selectedFilters: object, keys: object }): Observable<IndicatorEntry[]> {
    const url = '/api/enadis/v1/indicatorEntry/crossData';
    if (firstIndicator.selectedFilters['date']) {
      firstIndicator.selectedFilters['date'] = moment(firstIndicator.selectedFilters['date']);
    }
    if (firstIndicator.selectedFilters['dateStart'] && firstIndicator.selectedFilters['dateEnd']) {
      firstIndicator.selectedFilters['dateStart'] = moment(firstIndicator.selectedFilters['dateStart']);
      firstIndicator.selectedFilters['dateEnd'] = moment(firstIndicator.selectedFilters['dateEnd']);
    }
    if (secondIndicator.selectedFilters['date']) {
      secondIndicator.selectedFilters['date'] = moment(secondIndicator.selectedFilters['date']);
    }
    if (secondIndicator.selectedFilters['dateStart'] && secondIndicator.selectedFilters['dateEnd']) {
      secondIndicator.selectedFilters['dateStart'] = moment(secondIndicator.selectedFilters['dateStart']);
      secondIndicator.selectedFilters['dateEnd'] = moment(secondIndicator.selectedFilters['dateEnd']);
    }
    const params = {
      firstIndicator: firstIndicator,
      secondIndicator: secondIndicator
    };
    return this.http.post<IndicatorEntry[]>(url, params);
  }
}



// WEBPACK FOOTER //
// ./src/app/services/data-enadis-compare/data-enadis-compare.service.ts